import type {
  ReviewClosingBreakdownLine,
  ReviewClosingRow,
  ReviewClosingVariantLine,
} from '../../OrderForm/types'
import type { ProductApiData } from './types'
import { effectiveItemPrice } from '../fashionOrderDetailFromApi'
import { formatCurrency } from './utils'

const piecesOf = (product: ProductApiData): number => (product.sizeQuantities
  ? Object.values(product.sizeQuantities).reduce((sum, quantity) => sum + quantity, 0)
  : product.totalItems || 0)

/* `selectedColors` (ids) e `colors` (nomes) são arrays paralelos sem par id↔nome: pareamento posicional. */
const colorNameResolver = (product: ProductApiData): ((colorId: number) => string) => {
  const ids = product.selectedColors || []
  const names = product.colors || []
  if (names.length === 1) return () => names[0]
  if (ids.length !== names.length) return (colorId) => `Cor ${colorId}`
  return (colorId) => {
    const at = ids.indexOf(colorId)
    return at >= 0 ? names[at] : `Cor ${colorId}`
  }
}

export const buildFashionVariants = (product: ProductApiData): ReviewClosingVariantLine[] | undefined => {
  const grade = product.items || []
  if (grade.length === 0) return undefined

  const pieces = piecesOf(product)
  if (pieces <= 0) return undefined

  const registered = product.subtotal || 0
  const selling = product.sellingPrice != null ? Number(product.sellingPrice) : 0
  const unitPrice = (selling > 0 ? selling : registered) / pieces
  const colorNameOf = colorNameResolver(product)

  return grade.map((variant) => {
    const quantity = Number(variant.quantity) || 0
    return {
      color: colorNameOf(Number(variant.colorId)),
      size: String(variant.size),
      quantity,
      unitPrice,
      subtotal: quantity * unitPrice,
    }
  })
}

export const buildFashionClosingRows = (products: ProductApiData[]): ReviewClosingRow[] => (products || [])
  .map((product, index) => {
    const pieces = piecesOf(product)
    return {
      index,
      name: `${index + 1} · ${product.fashionProduct?.name || product.name || 'Item'}`,
      detail: product.kitIndex != null ? `Conjunto ${product.kitIndex + 1}` : undefined,
      pieces: pieces > 0 ? pieces : null,
      registeredPrice: product.subtotal || 0,
      sellingPrice: product.sellingPrice != null ? Number(product.sellingPrice) : null,
      variants: buildFashionVariants(product),
    }
  })

/** Composição do preço do fashion: peças pelo preço da tabela e, quando houver, o desconto. */
export const buildFashionBreakdown = (product: ProductApiData | undefined): ReviewClosingBreakdownLine[] => {
  if (!product) return []

  const lines: ReviewClosingBreakdownLine[] = []
  const pieces = piecesOf(product)
  const unitPrice = product.fashionProduct?.price ?? product.price ?? null

  lines.push({
    chip: 'Peças',
    desc: product.fashionProduct?.name || product.name || 'Produto',
    formula: pieces > 0 && unitPrice != null && unitPrice > 0
      ? `${pieces.toLocaleString('pt-BR')} × ${formatCurrency(unitPrice)}`
      : undefined,
    value: pieces > 0 && unitPrice != null && unitPrice > 0 ? pieces * unitPrice : null,
  })

  const desconto = (product.subtotal || 0) - effectiveItemPrice(product)
  if (Math.abs(desconto) >= 0.01) {
    const pct = ((product.subtotal || 0) > 0 ? (desconto / (product.subtotal || 1)) * 100 : 0)
    lines.push({
      chip: desconto < 0 ? 'Ajuste' : 'Desconto',
      desc: desconto < 0 ? 'Ajuste sobre o cadastrado' : 'Desconto aplicado',
      formula: `${Math.abs(pct).toFixed(1).replace('.', ',')}%`,
      value: Math.abs(desconto),
    })
  }

  return lines
}
